import React, { useEffect, useState } from 'react';
import { X, History, Clock, AlertTriangle, CheckCircle2, TrendingDown } from 'lucide-react';
import api from './axiosConfig';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة

const DowntimeHistoryModal = ({ isOpen, onClose, assetNum }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            const fetchHistory = async () => {
                setLoading(true);
                try {
                    const res = await api.get(`/assets/downtime/history/${assetNum}`);
                    setHistory(res.data);
                } catch (err) {
                    console.error("Failed to load downtime history");
                } finally {
                    setLoading(false);
                }
            };
            fetchHistory();
        }
    }, [isOpen, assetNum]);

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 z-[10004] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200 ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="bg-surface-card w-full max-w-5xl shadow-2xl rounded-sm border border-line flex flex-col max-h-[85vh]">

                {/* Header */}
                <div className={`bg-primary-dark text-ink-inverse p-4 flex justify-between items-center border-b-2 border-status-danger `}>
                    <div className={`flex items-center gap-3 `}>
                        <History size={20} className="text-status-danger" />
                        <div>
                            <h3 className="font-black text-xs uppercase tracking-widest">{t('downtime.history.title', 'Asset Downtime History')}</h3>
                            <p className="text-[10px] text-ink-inverse/70 font-bold uppercase">{t('downtime.history.subtitle', 'Tracking Performance for Asset')}: {assetNum}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="hover:bg-ink-inverse/10 p-1 rounded transition-colors"><X size={20}/></button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 bg-surface-panel custom-scrollbar">
                    {loading ? (
                        <div className="flex justify-center p-20 font-black text-ink-secondary animate-pulse uppercase tracking-widest">{t('common.loading', 'Retrieving History...')}</div>
                    ) : history.length === 0 ? (
                        <div className="text-center p-20 text-ink-secondary italic font-bold">{t('downtime.history.no_records', 'No downtime events recorded for this asset.')}</div>
                    ) : (
                        <div className="overflow-x-auto border bg-surface-card shadow-sm rounded-sm">
                            <table className={`w-full text-[11px] border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                                <thead className="bg-surface-panel border-b-2 border-line text-ink font-black uppercase tracking-tighter">
                                    <tr>
                                        <th className="p-3 border-x border-surface-panel w-10 text-center">{t('common.status', 'Status')}</th>
                                        <th className="p-3 border-x border-surface-panel">{t('downtime.start', 'Start Date')}</th>
                                        <th className="p-3 border-x border-surface-panel">{t('downtime.end', 'End Date')}</th>
                                        <th className="p-3 border-x border-surface-panel text-center">{t('downtime.history.duration_hrs', 'Duration (Hrs)')}</th>
                                        <th className="p-3 border-x border-surface-panel">{t('downtime.history.type_reason', 'Type/Reason')}</th>
                                        <th className="p-3 border-x border-surface-panel">{t('downtime.history.impact', 'Impact')}</th>
                                        <th className="p-3 border-x border-surface-panel">{t('downtime.history.reported_by', 'Reported By')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-line-subtle font-bold uppercase">
                                    {history.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-status-danger-bg/30 transition-colors">
                                            <td className="p-3 border-x border-line-subtle text-center">
                                                {item.enddate ?
                                                    <CheckCircle2 size={16} className="text-status-success mx-auto" /> :
                                                    <AlertTriangle size={16} className="text-status-danger animate-pulse mx-auto" />
                                                }
                                            </td>
                                            <td className="p-3 border-x border-line-subtle text-ink-secondary">
                                                {new Date(item.startdate).toLocaleString('sv-SE').slice(0, 16).replace('T', ' ')}
                                            </td>
                                            <td className="p-3 border-x border-line-subtle text-ink-secondary italic">
                                                {item.enddate ? new Date(item.enddate).toLocaleString('sv-SE').slice(0, 16).replace('T', ' ') : <span className="text-status-danger font-black animate-bounce">{t('downtime.history.still_down', 'STILL DOWN')}</span>}
                                            </td>
                                            <td className="p-3 border-x border-line-subtle text-center bg-surface-panel/50 font-black text-primary">
                                                {item.duration?.toFixed(2) || '0.00'}
                                            </td>
                                            <td className="p-3 border-x border-line-subtle">
                                                <div className="flex flex-col">
                                                    <span className="text-ink">{t(`downtime.types.${item.downtimetype?.toLowerCase()}`, item.downtimetype)}</span>
                                                    <span className="text-[9px] text-ink-secondary uppercase font-normal">{item.reasoncode}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 border-x border-line-subtle">
                                                {item.is_production_loss ?
                                                    <span className="bg-status-danger-bg text-status-danger px-2 py-0.5 rounded text-[9px] border border-status-danger/30 uppercase font-black flex items-center gap-1 w-fit">
                                                        <TrendingDown size={10} /> {t('downtime.history.prod_loss', 'Prod. Loss')}
                                                    </span> :
                                                    <span className="bg-status-success-bg text-status-success px-2 py-0.5 rounded text-[9px] border border-status-success/30 uppercase font-black w-fit block">{t('downtime.history.technical', 'Technical')}</span>
                                                }
                                            </td>
                                            <td className="p-3 border-x border-line-subtle text-ink-secondary text-[10px] uppercase italic">
                                                {item.changeby}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Summary Footer */}
                <div className={`bg-surface-card p-4 border-t flex justify-between items-center shadow-inner `}>
                    <div className={`text-[10px] text-ink-secondary font-bold uppercase flex items-center gap-4 `}>
                         <div className={`flex items-center gap-1.5 `}><div className="w-2 h-2 rounded-full bg-status-danger"></div> {t('downtime.history.active_down', 'Active Downtime')}</div>
                         <div className={`flex items-center gap-1.5 `}><div className="w-2 h-2 rounded-full bg-status-success"></div> {t('downtime.history.resolved', 'Resolved')}</div>
                    </div>
                    <button onClick={onClose} className="px-8 py-2 bg-primary-dark text-ink-inverse font-black text-[10px] uppercase hover:bg-primary transition-all rounded-sm">{t('common.close', 'Close History')}</button>
                </div>
            </div>
        </div>
    );
};

export default DowntimeHistoryModal;
